import React, { useState, useEffect } from "react";
import { useAppSelector } from "../../../../store/hooks";
import { formatNumber } from "../../../../utils/formatting";
import { selectors as optionalSystemsSelectors } from "../../../../store/selectors/optionalSystemsSelectors";

interface BreakawayCargoConfigProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SectionCargoAllocation {
  sectionNumber: number;
  sectionName: string;
  sectionSize: number;
  allocatedCargo: number;
  availableCargo: number;
  usedTonnage: number;
}

export const BreakawayCargoConfig: React.FC<BreakawayCargoConfigProps> = ({
  isOpen,
  onClose,
}) => {
  const hull = useAppSelector((state) => state.shipBuilder.hull);
  const shipSize = useAppSelector((state) => state.shipBuilder.shipSize);
  const totalTonnageUsed = useAppSelector(optionalSystemsSelectors.selectTotalOptionalSystemsTonnage);
  
  const [sectionAllocations, setSectionAllocations] = useState<SectionCargoAllocation[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Calculate total available cargo space for the whole ship
  const totalAvailableCargo = Math.max(0, shipSize - totalTonnageUsed);

  // Initialize allocations from existing configuration
  useEffect(() => {
    if (hull?.breakawayConfig?.sections) {
      const newAllocations: SectionCargoAllocation[] = [];

      // Calculate tonnage used per section (this is a simplified approach)
      // In a real implementation, you'd track tonnage used per section
      const totalSections = hull.breakawayConfig.sections.length;
      const tonnagePerSection = totalTonnageUsed / totalSections;

      hull.breakawayConfig.sections.forEach(section => {
        const sectionUsedTonnage = tonnagePerSection;
        const sectionAvailableCargo = Math.max(0, section.size - sectionUsedTonnage);
        
        newAllocations.push({
          sectionNumber: section.sectionNumber,
          sectionName: section.name || `Section ${section.sectionNumber}`,
          sectionSize: section.size,
          allocatedCargo: Math.floor(sectionAvailableCargo * 0.8), // Default to 80% of available
          availableCargo: sectionAvailableCargo,
          usedTonnage: sectionUsedTonnage,
        });
      });

      setSectionAllocations(newAllocations);
    }
  }, [hull?.breakawayConfig?.sections, totalTonnageUsed]);

  const handleCargoAllocationChange = (sectionNumber: number, value: number) => {
    setSectionAllocations(prev => 
      prev.map(allocation => 
        allocation.sectionNumber === sectionNumber
          ? { 
              ...allocation, 
              allocatedCargo: Math.max(0, Math.min(value, allocation.availableCargo))
            }
          : allocation
      )
    );
    setErrors(prev => ({ ...prev, [`section-${sectionNumber}`]: '' }));
  };

  const getTotalAllocatedCargo = () => {
    return sectionAllocations.reduce((sum, allocation) => sum + allocation.allocatedCargo, 0);
  };

  const validateAllocations = () => {
    const newErrors: Record<string, string> = {};
    const totalAllocated = getTotalAllocatedCargo();

    // Check if total allocated exceeds total available
    if (totalAllocated > totalAvailableCargo) {
      newErrors['total'] = `Total allocated cargo (${totalAllocated} tons) exceeds total available cargo (${totalAvailableCargo} tons)`;
    }

    // Check individual section allocations
    sectionAllocations.forEach(allocation => {
      if (allocation.allocatedCargo > allocation.availableCargo) {
        newErrors[`section-${allocation.sectionNumber}`] = `Allocated cargo exceeds available cargo for this section`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProportionalDistribution = () => {
    if (!hull?.breakawayConfig?.sections) return;

    const totalShipSize = hull.breakawayConfig.sections.reduce((total, section) => total + section.size, 0);
    
    const newAllocations = sectionAllocations.map(allocation => {
      const sectionRatio = allocation.sectionSize / totalShipSize;
      const proportionalCargo = Math.floor(totalAvailableCargo * sectionRatio);
      
      return {
        ...allocation,
        allocatedCargo: Math.min(proportionalCargo, allocation.availableCargo),
      };
    });

    setSectionAllocations(newAllocations);
    setErrors({});
  };

  const handleSave = () => {
    if (!validateAllocations()) {
      return;
    }

    // In a real implementation, you would save the cargo allocations to the store
    // For now, we'll just close the modal
    onClose();
  };

  if (!isOpen || !hull?.breakawayConfig?.sections) return null;

  const totalAllocated = getTotalAllocatedCargo();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Breakaway Cargo Configuration
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Breakaway Cargo Rules</h3>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Cargo space is calculated per breakaway section</li>
              <li>• Each section can have different cargo allocations</li>
              <li>• Total allocated cargo cannot exceed total available cargo</li>
              <li>• Individual section allocations cannot exceed section available cargo</li>
            </ul>
          </div>
        </div>

        {/* Ship-wide Summary */}
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Ship-wide Cargo Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-2 bg-white rounded border">
              <div className="font-medium text-gray-700">Total Ship Size</div>
              <div className="font-bold text-blue-600">{formatNumber(shipSize)} tons</div>
            </div>
            <div className="p-2 bg-white rounded border">
              <div className="font-medium text-gray-700">Total Used Tonnage</div>
              <div className="font-bold text-red-600">{formatNumber(totalTonnageUsed)} tons</div>
            </div>
            <div className="p-2 bg-white rounded border">
              <div className="font-medium text-gray-700">Total Available Cargo</div>
              <div className="font-bold text-green-600">{formatNumber(totalAvailableCargo)} tons</div>
            </div>
            <div className="p-2 bg-white rounded border">
              <div className="font-medium text-gray-700">Total Allocated Cargo</div>
              <div className={`font-bold ${totalAllocated <= totalAvailableCargo ? 'text-green-600' : 'text-red-600'}`}>
                {formatNumber(totalAllocated)} tons
              </div>
            </div>
          </div>
        </div>

        {/* Proportional Distribution Button */}
        <div className="mb-6">
          <button
            onClick={handleProportionalDistribution}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Distribute Proportionally by Section Size
          </button>
        </div>

        {/* Section Allocation Table */}
        <div className="space-y-6">
          {sectionAllocations.map((section) => (
            <div key={section.sectionNumber} className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">
                {section.sectionName} ({section.sectionSize} tons)
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Section Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Section Size
                  </label>
                  <div className="text-sm text-gray-600">
                    {formatNumber(section.sectionSize)} tons
                  </div>
                </div>

                {/* Used Tonnage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Used Tonnage
                  </label>
                  <div className="text-sm text-gray-600">
                    {formatNumber(section.usedTonnage)} tons
                  </div>
                </div>

                {/* Available Cargo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Available Cargo
                  </label>
                  <div className="text-sm text-gray-600">
                    {formatNumber(section.availableCargo)} tons
                  </div>
                </div>

                {/* Allocated Cargo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allocated Cargo
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={section.availableCargo}
                    value={section.allocatedCargo}
                    onChange={(e) => handleCargoAllocationChange(section.sectionNumber, parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  {errors[`section-${section.sectionNumber}`] && (
                    <div className="text-xs text-red-600 mt-1">
                      {errors[`section-${section.sectionNumber}`]}
                    </div>
                  )}
                </div>
              </div>

              {/* Section Progress Bar */}
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Cargo Usage</span>
                  <span>{Math.round((section.allocatedCargo / section.availableCargo) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((section.allocatedCargo / section.availableCargo) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Error Messages */}
        {Object.keys(errors).length > 0 && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <h4 className="text-sm font-semibold text-red-800 mb-2">Allocation Errors</h4>
            <ul className="text-sm text-red-700 space-y-1">
              {Object.entries(errors).map(([key, error]) => (
                <li key={key}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 mr-3"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={Object.keys(errors).length > 0}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
}; 